/**
 * Capital gain calculations for sold fixed assets.
 * CPI / indexIncrease comes from the backend API — not recomputed here.
 */

/** Round to nearest integer (matches Hisulit example: 293.38 → 293). */
export function roundInflationaryGain(value) {
    return Math.round(Number(value) || 0);
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function toDateOnly(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysInYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
}

/** Matches AssetService.ClampDepreciation. */
export function clampDepreciation(amount, originalPrice) {
    const absMax = Math.abs(Number(originalPrice) || 0);
    const clamped = Math.min(Math.max(Number(amount) || 0, 0), absMax);
    return (Number(originalPrice) || 0) < 0 ? -clamped : clamped;
}

/** Form 1342 current sale-year days from 01/01 to sale date (30/360). */
export function calculateForm1342CurrentYearDays(saleDate) {
    const sale = toDateOnly(saleDate);
    if (!sale) return 0;
    return (sale.getMonth() * 30) + (sale.getDate() - 1);
}

/** 30/360 day count between two dates (Form 1342 style). */
export function calculateForm1342DaysBetween(startDate, endDate) {
    const start = toDateOnly(startDate);
    const end = toDateOnly(endDate);
    if (!start || !end) return 0;
    return (
        (end.getFullYear() - start.getFullYear()) * 360 +
        (end.getMonth() - start.getMonth()) * 30 +
        (end.getDate() - start.getDate())
    );
}

function roundShekel(value) {
    return Math.round(Number(value) || 0);
}

/**
 * Hisulit / Form 1342 depreciation at sale: prior full tax years + current-year 30/360 proration.
 */
export function calculateForm1342DepreciationAtSale({
    originalPrice,
    depreciationPercentage,
    activationDate,
    purchaseDate,
    saleDate,
}) {
    const depStart =
        toDateOnly(activationDate) || toDateOnly(purchaseDate);
    const sale = toDateOnly(saleDate);
    const absOriginal = Math.abs(Number(originalPrice) || 0);
    const sign = Number(originalPrice) < 0 ? -1 : 1;
    const rate = Number(depreciationPercentage) || 0;

    const empty = {
        previousYearsDepreciation: 0,
        currentYearDepreciation: 0,
        totalDepreciation: 0,
        remainingCost: Number(originalPrice) || 0,
        currentYearClaimedPercent: 0,
        annualDepreciationAmount: 0,
        fullPreviousYears: 0,
        currentYearDepreciationDays: 0,
        depreciationDayCountMethod: '30/360',
    };

    if (!depStart || !sale || absOriginal === 0 || rate === 0 || sale < depStart) {
        return empty;
    }

    const annualDepreciation = absOriginal * (rate / 100);
    let previousYearsDepreciationRaw = 0;
    let currentYearDepreciationRaw = 0;
    let fullPreviousYears = 0;
    let currentYearDays = 0;

    if (depStart.getFullYear() === sale.getFullYear()) {
        currentYearDays = calculateForm1342DaysBetween(depStart, sale);
        currentYearDepreciationRaw = (annualDepreciation * currentYearDays) / 360;
    } else if (depStart.getMonth() === 0 && depStart.getDate() === 1) {
        fullPreviousYears = sale.getFullYear() - depStart.getFullYear();
        previousYearsDepreciationRaw = annualDepreciation * fullPreviousYears;
        currentYearDays = calculateForm1342CurrentYearDays(sale);
        currentYearDepreciationRaw = (annualDepreciation * currentYearDays) / 360;
    } else {
        const endOfActivationYear = new Date(depStart.getFullYear(), 11, 31);
        const activationYearDays = calculateForm1342DaysBetween(depStart, endOfActivationYear);
        const activationYearPartialRaw = (annualDepreciation * activationYearDays) / 360;
        fullPreviousYears = Math.max(0, sale.getFullYear() - depStart.getFullYear() - 1);
        previousYearsDepreciationRaw =
            annualDepreciation * fullPreviousYears + activationYearPartialRaw;
        currentYearDays = calculateForm1342CurrentYearDays(sale);
        currentYearDepreciationRaw = (annualDepreciation * currentYearDays) / 360;
    }

    const totalDepreciationRaw = previousYearsDepreciationRaw + currentYearDepreciationRaw;
    const totalDepreciation = Math.min(roundShekel(totalDepreciationRaw), absOriginal);
    const previousYearsDepreciationDisplay = roundShekel(previousYearsDepreciationRaw);
    const currentYearDepreciationDisplay = roundShekel(currentYearDepreciationRaw);

    return {
        previousYearsDepreciationRaw: previousYearsDepreciationRaw * sign,
        currentYearDepreciationRaw: currentYearDepreciationRaw * sign,
        totalDepreciationRaw: totalDepreciationRaw * sign,
        previousYearsDepreciationDisplay: previousYearsDepreciationDisplay * sign,
        currentYearDepreciationDisplay: currentYearDepreciationDisplay * sign,
        previousYearsDepreciation: previousYearsDepreciationDisplay * sign,
        currentYearDepreciation: currentYearDepreciationDisplay * sign,
        totalDepreciation: totalDepreciation * sign,
        remainingCost: (absOriginal - totalDepreciation) * sign,
        currentYearClaimedPercent:
            absOriginal === 0
                ? 0
                : Math.round((currentYearDepreciationRaw / absOriginal) * 100 * 1e6) / 1e6,
        annualDepreciationAmount: annualDepreciation * sign,
        fullPreviousYears,
        currentYearDepreciationDays: currentYearDays,
        depreciationDayCountMethod: '30/360',
    };
}

/**
 * Legacy day-based depreciation (365/366) — fallback/debug only, not Hisulit Form 1342.
 */
export function calculateDepreciationFromActivationToDate({
    originalPrice,
    depreciationPercentage,
    activationDate,
    endDate,
}) {
    const activation = toDateOnly(activationDate);
    const end = toDateOnly(endDate);

    if (!activation || !end || end < activation) return 0;

    const annualDepreciation =
        Math.abs(Number(originalPrice) || 0) * (Number(depreciationPercentage) || 0) / 100;
    const activationYear = activation.getFullYear();
    const endYear = end.getFullYear();

    if (activationYear === endYear) {
        const daysActive = Math.floor((end - activation) / MS_PER_DAY) + 1;
        if (daysActive <= 0) return 0;
        const fraction = Math.min(1, daysActive / daysInYear(activationYear));
        return clampDepreciation(fraction * annualDepreciation, originalPrice);
    }

    const activationYearEnd = new Date(activationYear, 11, 31);
    const daysInActivationYear =
        Math.floor((activationYearEnd - activation) / MS_PER_DAY) + 1;
    const fractionActivation =
        daysInActivationYear > 0
            ? Math.min(1, daysInActivationYear / daysInYear(activationYear))
            : 0;
    const depActivationYear = fractionActivation * annualDepreciation;

    const fullYearsCount = endYear - activationYear - 1;
    const fullYearsDep = fullYearsCount > 0 ? annualDepreciation * fullYearsCount : 0;

    const startOfEndYear = new Date(endYear, 0, 1);
    const daysInEndYear = Math.floor((end - startOfEndYear) / MS_PER_DAY) + 1;
    const fractionEndYear = Math.min(1, daysInEndYear / daysInYear(endYear));
    const depLastYear = fractionEndYear * annualDepreciation;

    const total = depActivationYear + fullYearsDep + depLastYear;
    return clampDepreciation(total, originalPrice);
}

/**
 * Child enhancement depreciation at sale — uses the child's depreciation rate when set,
 * otherwise falls back to the parent rate.
 */
export function calculateChildEnhancementDepreciation({
    childOriginalPrice,
    childPurchaseDate,
    childActivationDate,
    saleDate,
    depreciationPercentage,
    parentDepreciationPercentage,
}) {
    const originalPrice = Number(childOriginalPrice) || 0;
    const childRate = depreciationPercentage ?? parentDepreciationPercentage;
    const rate = Number(childRate) || 0;
    const activation = toDateOnly(childActivationDate || childPurchaseDate);
    const sale = toDateOnly(saleDate);

    if (!originalPrice || !rate || !activation || !sale) {
        return {
            childAccumulatedDepreciation: 0,
            childRemainingOriginalCost: originalPrice,
        };
    }

    const form1342 = calculateForm1342DepreciationAtSale({
        originalPrice,
        depreciationPercentage: rate,
        activationDate: childActivationDate || childPurchaseDate,
        purchaseDate: childPurchaseDate,
        saleDate: sale,
    });

    return {
        childAccumulatedDepreciation: form1342.totalDepreciation,
        childRemainingOriginalCost: form1342.remainingCost,
    };
}

export function mapSavedCapitalGainEnhancementToChild(savedRow) {
    const enhancementAssetId =
        savedRow.enhancementAssetId ?? savedRow.EnhancementAssetId ?? savedRow.id ?? savedRow.Id;
    const originalPrice = Number(savedRow.originalPrice ?? savedRow.OriginalPrice ?? 0);
    const accumulatedDepreciationAtSale = Number(
        savedRow.accumulatedDepreciationAtSale ?? savedRow.AccumulatedDepreciationAtSale ?? 0
    );
    const remainingOriginalCostAtSale = Number(
        savedRow.remainingOriginalCostAtSale ?? savedRow.RemainingOriginalCostAtSale ?? 0
    );
    const saleDate = savedRow.saleDate ?? savedRow.SaleDate ?? null;
    const saleYear =
        saleDate != null && saleDate !== ''
            ? new Date(saleDate).getFullYear()
            : savedRow.saleYear ?? savedRow.SaleYear ?? null;

    return {
        id: enhancementAssetId,
        Id: enhancementAssetId,
        assetNumber: savedRow.assetNumber ?? savedRow.AssetNumber,
        assetName: savedRow.name ?? savedRow.Name,
        name: savedRow.name ?? savedRow.Name,
        description: savedRow.description ?? savedRow.Description,
        purchaseDate: savedRow.purchaseDate ?? savedRow.PurchaseDate,
        activationDate: savedRow.activationDate ?? savedRow.ActivationDate,
        originalPrice,
        OriginalPrice: originalPrice,
        accumulatedDepreciationAtSale,
        AccumulatedDepreciationAtSale: accumulatedDepreciationAtSale,
        remainingOriginalCost: remainingOriginalCostAtSale,
        RemainingOriginalCost: remainingOriginalCostAtSale,
        remainingOriginalCostAtSale,
        RemainingOriginalCostAtSale: remainingOriginalCostAtSale,
        saleDate,
        SaleDate: saleDate,
        saleYear,
        SaleYear: saleYear,
        depreciationPercentage:
            savedRow.depreciationPercentage ?? savedRow.DepreciationPercentage,
    };
}

export function computeSelectedEnhancementsTotals({
    selectedChildren = [],
    saleDate,
    parentDepreciationPercentage,
}) {
    return selectedChildren.reduce(
        (acc, child) => {
            const backendRemaining =
                child.remainingOriginalCostAtSale ??
                child.RemainingOriginalCostAtSale ??
                child.remainingOriginalCost ??
                child.RemainingOriginalCost;
            const backendDepreciation =
                child.accumulatedDepreciationAtSale ?? child.AccumulatedDepreciationAtSale;

            let childRemainingOriginalCost;
            let childAccumulatedDepreciation;

            if (backendRemaining != null && backendRemaining !== '') {
                childRemainingOriginalCost = Number(backendRemaining) || 0;
                childAccumulatedDepreciation =
                    backendDepreciation != null && backendDepreciation !== ''
                        ? Number(backendDepreciation) || 0
                        : Math.max(
                              0,
                              Number(child.originalPrice ?? child.OriginalPrice ?? 0) -
                                  childRemainingOriginalCost
                          );
            } else {
                const calculated = calculateChildEnhancementDepreciation({
                    childOriginalPrice: child.originalPrice ?? child.OriginalPrice,
                    childPurchaseDate: child.purchaseDate ?? child.PurchaseDate,
                    childActivationDate: child.activationDate ?? child.ActivationDate,
                    saleDate,
                    depreciationPercentage:
                        child.depreciationPercentage ?? child.DepreciationPercentage,
                    parentDepreciationPercentage,
                });
                childRemainingOriginalCost = calculated.childRemainingOriginalCost;
                childAccumulatedDepreciation = calculated.childAccumulatedDepreciation;
            }

            return {
                enhancementsBalance: acc.enhancementsBalance + childRemainingOriginalCost,
                enhancementsAccumulatedDepreciation:
                    acc.enhancementsAccumulatedDepreciation + childAccumulatedDepreciation,
            };
        },
        { enhancementsBalance: 0, enhancementsAccumulatedDepreciation: 0 }
    );
}

/** V1 default tax rates (Hisulit screen defaults — configurable, not hardcoded in UI). */
export const CAPITAL_GAIN_DEFAULT_TAX_RATES = {
    inflationaryTaxableRate: 10,
    realFrom2003Rate: 20,
    realUntil2002Rate: 0,
};

/** V1 frontend config by tax year. Prefer backend by year / taxpayer type later. */
export const CAPITAL_GAIN_TAX_CONFIG = {
    default: { ...CAPITAL_GAIN_DEFAULT_TAX_RATES },
    byYear: {
        2026: { ...CAPITAL_GAIN_DEFAULT_TAX_RATES },
    },
};

export function getCapitalGainTaxRatesForYear(taxYear, taxConfig = CAPITAL_GAIN_TAX_CONFIG) {
    const year = Number(taxYear);
    const yearRates =
        !Number.isNaN(year) && taxConfig?.byYear?.[year] ? taxConfig.byYear[year] : {};

    return {
        inflationaryTaxableRate:
            yearRates.inflationaryTaxableRate ??
            taxConfig?.default?.inflationaryTaxableRate ??
            CAPITAL_GAIN_DEFAULT_TAX_RATES.inflationaryTaxableRate,
        realFrom2003Rate:
            yearRates.realFrom2003Rate ??
            taxConfig?.default?.realFrom2003Rate ??
            CAPITAL_GAIN_DEFAULT_TAX_RATES.realFrom2003Rate,
        realUntil2002Rate:
            yearRates.realUntil2002Rate ??
            taxConfig?.default?.realUntil2002Rate ??
            CAPITAL_GAIN_DEFAULT_TAX_RATES.realUntil2002Rate,
    };
}

/**
 * Splits positive real gain between pre-2003 and from-2003 buckets (Hisulit manual).
 */
export function splitRealGainByPeriods({ realGain, purchaseDate, saleDate }) {
    if (realGain <= 0) {
        return {
            realFrom2003: 0,
            realUntil2002: 0,
        };
    }

    const start = new Date(purchaseDate);
    const end = new Date(saleDate);
    const cutoff = new Date('2003-01-01');

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return {
            realFrom2003: Math.round(realGain),
            realUntil2002: 0,
        };
    }

    if (start >= cutoff) {
        return {
            realFrom2003: Math.round(realGain),
            realUntil2002: 0,
        };
    }

    if (end < cutoff) {
        return {
            realFrom2003: 0,
            realUntil2002: Math.round(realGain),
        };
    }

    const totalDays = Math.max((end - start) / (1000 * 60 * 60 * 24), 1);
    const pre2003Days = Math.max((cutoff - start) / (1000 * 60 * 60 * 24), 0);
    const until2002Ratio = pre2003Days / totalDays;

    const realUntil2002 = Math.round(realGain * until2002Ratio);
    const realFrom2003 = Math.round(realGain) - realUntil2002;

    return {
        realFrom2003,
        realUntil2002,
    };
}

/**
 * Computes capital gain breakdown from asset basis + API index increase.
 *
 * Cases:
 * - C: indexIncrease <= 0 — no inflationary split; real gain/loss follows totalCapitalGain only.
 * - A: totalCapitalGain <= 0 with index — inflationary amount is exempt display only; realLoss includes it; no tax.
 * - B: totalCapitalGain > 0 — inflationary exempt up to total gain; V1 keeps inflationaryTaxableGain = 0.
 */
export function computeCapitalGains({
    salePrice,
    originalPrice,
    accumulatedDepreciation,
    enhancementsBalance = 0,
    enhancementsAccumulatedDepreciation = 0,
    saleExpenses = 0,
    indexIncrease = 0,
    purchaseDate,
    saleDate,
}) {
    const original = Math.round(Number(originalPrice) || 0);
    const accumulated = Math.round(Number(accumulatedDepreciation) || 0);
    const enhancements = Math.round(Number(enhancementsBalance) || 0);
    const enhancementsDep = Math.round(Number(enhancementsAccumulatedDepreciation) || 0);
    const sale = Number(salePrice) || 0;
    const expenses = Number(saleExpenses) || 0;
    const idx = Number(indexIncrease) || 0;

    const remainingOriginalCost = original - accumulated + enhancements;
    const totalCapitalGain = sale - remainingOriginalCost - expenses;
    const inflationaryGain = roundInflationaryGain(remainingOriginalCost * idx);

    let inflationaryExemptGain = 0;
    let inflationaryTaxableGain = 0;
    let realGain = 0;
    let taxableRealGain = 0;
    let realLoss = 0;
    let realFrom2003 = 0;
    let realUntil2002 = 0;

    if (idx <= 0) {
        inflationaryExemptGain = 0;
        inflationaryTaxableGain = 0;
        realGain = Math.max(totalCapitalGain, 0);
        taxableRealGain = realGain;
        realLoss = totalCapitalGain < 0 ? Math.abs(totalCapitalGain) : 0;
    } else if (totalCapitalGain <= 0) {
        inflationaryExemptGain = Math.max(inflationaryGain, 0);
        inflationaryTaxableGain = 0;
        taxableRealGain = 0;
        realGain = 0;
        realLoss = Math.abs(totalCapitalGain) + inflationaryExemptGain;
    } else {
        const inflationaryAmount = Math.max(inflationaryGain, 0);
        inflationaryExemptGain = Math.min(totalCapitalGain, inflationaryAmount);
        inflationaryTaxableGain = 0;
        realGain = Math.max(
            totalCapitalGain - inflationaryExemptGain - inflationaryTaxableGain,
            0
        );
        taxableRealGain = realGain;
        realLoss = 0;
    }

    if (realGain > 0) {
        const split = splitRealGainByPeriods({ realGain, purchaseDate, saleDate });
        realFrom2003 = split.realFrom2003;
        realUntil2002 = split.realUntil2002;
    }

    return {
        remainingOriginalCost,
        enhancementsBalance: enhancements,
        enhancementsAccumulatedDepreciation: enhancementsDep,
        totalAccumulatedDepreciation: accumulated + enhancementsDep,
        totalCapitalGain,
        inflationaryGain,
        inflationaryExemptGain,
        inflationaryTaxableGain,
        realGain,
        taxableRealGain,
        realFrom2003,
        realUntil2002,
        realLoss,
    };
}

/** Real gain buckets available for Section 96 replacement after current enhancements. */
export function getAvailableRealGainForReplacement(gains) {
    return (
        Math.max(Number(gains?.realFrom2003 || 0), 0) +
        Math.max(Number(gains?.realUntil2002 || 0), 0)
    );
}

/** Inflationary taxable gain available for Section 96 replacement after current enhancements. */
export function getAvailableInflationaryGainForReplacement(gains) {
    return Math.max(Number(gains?.inflationaryTaxableGain || 0), 0);
}

/** Whether persisted replacement totals exceed gains after enhancements changed. */
export function isReplacementInvalidAfterEnhancements(replacementData, gains) {
    const totalRealReplacementAmount = Number(replacementData?.realReplacementAmount || 0);
    const totalInflationaryReplacementAmount = Number(
        replacementData?.inflationaryReplacementAmount || 0
    );

    return (
        totalRealReplacementAmount > getAvailableRealGainForReplacement(gains) ||
        totalInflationaryReplacementAmount > getAvailableInflationaryGainForReplacement(gains)
    );
}

/**
 * Splits a single real replacement amount across from-2003 and until-2002 buckets (Hisulit).
 * Clamps the total to available real gain so display/tax never exceed taxable buckets.
 */
export function splitRealReplacementAmount({
    realReplacementAmount,
    realFrom2003,
    realUntil2002,
}) {
    const maxAvailableRealGain =
        Math.max(Number(realFrom2003 || 0), 0) +
        Math.max(Number(realUntil2002 || 0), 0);

    let remaining = Math.min(
        Math.max(Number(realReplacementAmount || 0), 0),
        maxAvailableRealGain
    );

    const replacementFrom2003 = Math.min(
        Math.max(Number(realFrom2003 || 0), 0),
        remaining
    );

    remaining -= replacementFrom2003;

    const replacementUntil2002 = Math.min(
        Math.max(Number(realUntil2002 || 0), 0),
        remaining
    );

    remaining -= replacementUntil2002;

    return {
        replacementFrom2003,
        replacementUntil2002,
        unappliedReplacement: remaining,
    };
}

export function buildReplacementSummary({ replacementData, gains }) {
    const safeRealReplacementAmount = Math.min(
        Math.max(Number(replacementData?.realReplacementAmount || 0), 0),
        getAvailableRealGainForReplacement(gains)
    );
    const safeInflationaryReplacementAmount = Math.min(
        Math.max(Number(replacementData?.inflationaryReplacementAmount || 0), 0),
        getAvailableInflationaryGainForReplacement(gains)
    );

    const { replacementFrom2003, replacementUntil2002, unappliedReplacement } =
        splitRealReplacementAmount({
            realReplacementAmount: safeRealReplacementAmount,
            realFrom2003: gains?.realFrom2003,
            realUntil2002: gains?.realUntil2002,
        });

    return {
        realReplacementAmount: safeRealReplacementAmount,
        inflationaryReplacementAmount: safeInflationaryReplacementAmount,
        replacementFrom2003,
        replacementUntil2002,
        unappliedReplacement,
    };
}

/** Build API save rows with per-row real gain bucket splits (Hisulit). */
export function buildReplacementSaveRows(replacementRows = [], gains = {}) {
    let remainingFrom2003 = Math.max(Number(gains?.realFrom2003 || 0), 0);
    let remainingUntil2002 = Math.max(Number(gains?.realUntil2002 || 0), 0);

    return replacementRows.map((row) => {
        const real = Number(row.realReplacementAmount || 0);
        const from2003 = Math.min(remainingFrom2003, real);
        remainingFrom2003 -= from2003;
        const until2002 = Math.min(remainingUntil2002, Math.max(real - from2003, 0));
        remainingUntil2002 -= until2002;

        return {
            replacementAssetId: row.assetId,
            realReplacementAmount: real,
            inflationaryReplacementAmount: Number(row.inflationaryReplacementAmount || 0),
            replacementFrom2003: from2003,
            replacementUntil2002: until2002,
        };
    });
}

export function getBeforeReplacementCost(row) {
    const remaining = row?.remainingOriginalCost ?? row?.remainingCost;
    if (remaining != null && remaining !== '') {
        return Number(remaining) || 0;
    }
    return Number(row?.originalPrice ?? 0);
}

export function computeAdjustedGainBeforeTax(gains, adjustedGains) {
    if (!gains || !adjustedGains) return 0;
    return (
        Number(adjustedGains.adjustedInflationaryTaxableGain || 0) +
        Number(adjustedGains.adjustedRealFrom2003 || 0) +
        Number(adjustedGains.adjustedRealUntil2002 || 0) +
        Math.max(Number(gains.inflationaryExemptGain || 0), 0) -
        Math.max(Number(gains.realLoss || 0), 0)
    );
}

export function mapSavedReplacementToRow(savedRow, context = {}) {
    const originalPrice = Number(
        savedRow.replacementAssetOriginalPrice ??
            savedRow.ReplacementAssetOriginalPrice ??
            savedRow.replacementAssetOriginalCostBefore ??
            savedRow.ReplacementAssetOriginalCostBefore ??
            0
    );
    const remainingCost = Number(
        savedRow.replacementAssetOriginalCostBefore ??
            savedRow.ReplacementAssetOriginalCostBefore ??
            0
    );

    const asset = {
        id: savedRow.replacementAssetId ?? savedRow.ReplacementAssetId,
        assetNumber: savedRow.replacementAssetNumber ?? savedRow.ReplacementAssetNumber,
        name: savedRow.replacementAssetName ?? savedRow.ReplacementAssetName,
        purchaseDate: savedRow.replacementAssetPurchaseDate ?? savedRow.ReplacementAssetPurchaseDate,
        originalPrice,
        remainingOriginalCost: remainingCost,
        realReplacementAmount:
            savedRow.realReplacementAmount ?? savedRow.RealReplacementAmount,
        inflationaryReplacementAmount:
            savedRow.inflationaryReplacementAmount ?? savedRow.InflationaryReplacementAmount,
        replacementFrom2003: savedRow.replacementFrom2003 ?? savedRow.ReplacementFrom2003,
        replacementUntil2002: savedRow.replacementUntil2002 ?? savedRow.ReplacementUntil2002,
    };

    const mapped = mapAssetToReplacementRow(asset, context);
    return {
        ...mapped,
        id: savedRow.id ?? savedRow.Id ?? null,
        replacementId: savedRow.id ?? savedRow.Id ?? null,
        originalPrice: originalPrice || mapped.originalPrice,
        remainingCost: remainingCost || mapped.remainingCost,
        replacementFrom2003: Number(asset.replacementFrom2003 || 0),
        replacementUntil2002: Number(asset.replacementUntil2002 || 0),
    };
}

export function mapSavedReplacementsToReplacementData(savedRows = [], context = {}) {
    const selectedAssets = (savedRows || []).map((row) => mapSavedReplacementToRow(row, context));
    const totals = computeReplacementTotals(selectedAssets);
    return {
        selectedAssets,
        realReplacementAmount: totals.totalRealReplacementAmount,
        inflationaryReplacementAmount: totals.totalInflationaryReplacementAmount,
    };
}

export function hasAppliedReplacement(replacementSummary) {
    return (
        Number(replacementSummary?.realReplacementAmount || 0) > 0 ||
        Number(replacementSummary?.inflationaryReplacementAmount || 0) > 0
    );
}

/** Whether any taxable/real bucket exists for Section 96 replacement (V1). */
export function hasReplaceableTaxableGain(gains) {
    if (!gains) return false;
    return (
        Number(gains.realFrom2003 || 0) > 0 ||
        Number(gains.realUntil2002 || 0) > 0 ||
        Number(gains.inflationaryTaxableGain || 0) > 0
    );
}

export function computeAdjustedGainsAfterReplacement(gains, replacementSummary) {
    if (!gains) {
        return {
            adjustedRealFrom2003: 0,
            adjustedRealUntil2002: 0,
            adjustedInflationaryTaxableGain: 0,
        };
    }

    const replacementFrom2003 = Number(replacementSummary?.replacementFrom2003 || 0);
    const replacementUntil2002 = Number(replacementSummary?.replacementUntil2002 || 0);
    const inflationaryReplacementAmount = Number(
        replacementSummary?.inflationaryReplacementAmount || 0
    );

    return {
        adjustedRealFrom2003: Math.max(Number(gains.realFrom2003 || 0) - replacementFrom2003, 0),
        adjustedRealUntil2002: Math.max(Number(gains.realUntil2002 || 0) - replacementUntil2002, 0),
        adjustedInflationaryTaxableGain: Math.max(
            Number(gains.inflationaryTaxableGain || 0) - inflationaryReplacementAmount,
            0
        ),
    };
}

export function getReplacementReductionAsOfDate(asset, saleDate) {
    const activation = toDateOnly(asset?.activationDate ?? asset?.ActivationDate ?? asset?.purchaseDate ?? asset?.PurchaseDate);
    const sale = toDateOnly(saleDate);
    if (!sale) return saleDate;
    if (!activation || sale >= activation) return saleDate;

    // Not yet active at sale — if it activates later in the same tax year, evaluate at year-end.
    if (activation.getFullYear() === sale.getFullYear()) {
        return new Date(sale.getFullYear(), 11, 31);
    }

    return saleDate;
}

export function getAssetReductionBalanceValues(asset, { saleDate, parentDepreciationPercentage } = {}) {
    const backendRemaining = asset?.remainingOriginalCost ?? asset?.RemainingOriginalCost;
    const backendDepreciation =
        asset?.accumulatedDepreciationAtSale ?? asset?.AccumulatedDepreciationAtSale;
    const originalPrice = Number(asset?.originalPrice ?? asset?.OriginalPrice ?? 0);

    if (backendRemaining != null && backendRemaining !== '') {
        const remainingOriginalCost = Number(backendRemaining) || 0;
        const accumulatedDepreciation =
            backendDepreciation != null && backendDepreciation !== ''
                ? Number(backendDepreciation) || 0
                : Math.max(0, originalPrice - remainingOriginalCost);
        return { remainingOriginalCost, accumulatedDepreciation };
    }

    const depreciationPercentage =
        asset?.depreciationPercentage ?? asset?.DepreciationPercentage ?? parentDepreciationPercentage;
    const evaluationDate = getReplacementReductionAsOfDate(asset, saleDate);

    const calculated = calculateChildEnhancementDepreciation({
        childOriginalPrice: originalPrice,
        childPurchaseDate: asset?.purchaseDate ?? asset?.PurchaseDate,
        childActivationDate: asset?.activationDate ?? asset?.ActivationDate,
        saleDate: evaluationDate,
        depreciationPercentage,
        parentDepreciationPercentage,
    });

    return {
        remainingOriginalCost: calculated.childRemainingOriginalCost,
        accumulatedDepreciation: calculated.childAccumulatedDepreciation,
    };
}

export function getAssetId(asset) {
    return asset?.assetId ?? asset?.id ?? asset?.Id;
}

export function getAssetYearFromDate(purchaseDate, fallbackYear) {
    if (purchaseDate) {
        const d = new Date(purchaseDate);
        if (!Number.isNaN(d.getTime())) return d.getFullYear();
    }
    const year = Number(fallbackYear);
    return Number.isFinite(year) ? year : null;
}

/** Row basis for replacement clamping: remaining cost before replacement. */
export function getReplacementRowMaxBasis(row) {
    return getBeforeReplacementCost(row);
}

export function mapAssetToReplacementRow(asset, context = {}) {
    const purchaseDate = asset?.purchaseDate || asset?.PurchaseDate;
    const originalPrice = Number(asset?.originalPrice ?? asset?.OriginalPrice ?? 0);
    const { remainingOriginalCost } = context?.saleDate
        ? getAssetReductionBalanceValues(asset, context)
        : {
              remainingOriginalCost: Number(
                  asset?.remainingOriginalCost ??
                      asset?.RemainingOriginalCost ??
                      asset?.remainingCost ??
                      0
              ),
          };

    return {
        assetId: getAssetId(asset),
        assetNumber: asset?.assetNumber ?? asset?.AssetNumber ?? '',
        assetName: asset?.assetName ?? asset?.AssetName ?? asset?.name ?? asset?.Name ?? '',
        purchaseDate,
        year: getAssetYearFromDate(purchaseDate, asset?.year ?? asset?.Year),
        originalPrice,
        remainingCost: remainingOriginalCost,
        realReplacementAmount: Number(asset?.realReplacementAmount || 0),
        inflationaryReplacementAmount: Number(asset?.inflationaryReplacementAmount || 0),
    };
}

/** After replacement = לפני שחלוף נוכחי minus row replacement amounts (Hisulit). */
export function computeAfterReplacementCost(row) {
    const beforeBasis = getBeforeReplacementCost(row);
    const totalReplacement =
        Number(row?.realReplacementAmount || 0) + Number(row?.inflationaryReplacementAmount || 0);
    return Math.max(beforeBasis - totalReplacement, 0);
}

export function computeReplacementTotals(replacementRows = []) {
    const totalRealReplacementAmount = replacementRows.reduce(
        (sum, row) => sum + Number(row.realReplacementAmount || 0),
        0
    );
    const totalInflationaryReplacementAmount = replacementRows.reduce(
        (sum, row) => sum + Number(row.inflationaryReplacementAmount || 0),
        0
    );

    return {
        totalRealReplacementAmount,
        totalInflationaryReplacementAmount,
        totalReplacement: totalRealReplacementAmount + totalInflationaryReplacementAmount,
    };
}

/**
 * Clamp a row replacement field against row basis and available gain buckets.
 */
export function clampReplacementRowField({
    row,
    field,
    rawValue,
    replacementRows,
    maxRealGain,
    maxInflationaryGain,
}) {
    const assetId = getAssetId(row);
    const requested = Number(rawValue);
    let errorCode = null;

    if (Number.isNaN(requested) || requested < 0) {
        return {
            realReplacementAmount: Number(row.realReplacementAmount || 0),
            inflationaryReplacementAmount: Number(row.inflationaryReplacementAmount || 0),
            errorCode: 'replacementNegative',
        };
    }

    const value = Math.max(requested, 0);

    const otherRealTotal = replacementRows.reduce((sum, r) => {
        if (getAssetId(r) === assetId) return sum;
        return sum + Number(r.realReplacementAmount || 0);
    }, 0);

    const otherInflationaryTotal = replacementRows.reduce((sum, r) => {
        if (getAssetId(r) === assetId) return sum;
        return sum + Number(r.inflationaryReplacementAmount || 0);
    }, 0);

    const beforeBasis = getBeforeReplacementCost(row);
    const currentReal =
        field === 'realReplacementAmount' ? value : Number(row.realReplacementAmount || 0);
    const currentInflationary =
        field === 'inflationaryReplacementAmount'
            ? value
            : Number(row.inflationaryReplacementAmount || 0);

    let real = currentReal;
    let inflationary = currentInflationary;

    if (field === 'realReplacementAmount') {
        const maxAllowed = Math.max(maxRealGain - otherRealTotal, 0);
        if (value > maxAllowed) errorCode = 'realExceedsGain';
        real = Math.min(value, maxAllowed);
    } else {
        const maxAllowed = Math.max(maxInflationaryGain - otherInflationaryTotal, 0);
        if (value > maxAllowed) errorCode = 'inflationaryExceedsGain';
        inflationary = Math.min(value, maxAllowed);
    }

    const rowTotal = real + inflationary;
    if (rowTotal > beforeBasis) {
        errorCode = 'exceedsBeforeBasis';
        const overflow = rowTotal - beforeBasis;
        if (field === 'realReplacementAmount') {
            real = Math.max(real - overflow, 0);
        } else {
            inflationary = Math.max(inflationary - overflow, 0);
        }
    }

    return {
        realReplacementAmount: real,
        inflationaryReplacementAmount: inflationary,
        errorCode,
    };
}

/**
 * Tax summary per Hisulit columns. Never tax raw inflationaryGain.
 * When totalCapitalGain <= 0, all tax amounts are zero.
 */
export function computeTaxSummary({
    totalCapitalGain,
    inflationaryTaxableGain = 0,
    realFrom2003 = 0,
    realUntil2002 = 0,
    inflationaryTaxRate = CAPITAL_GAIN_DEFAULT_TAX_RATES.inflationaryTaxableRate,
    realFrom2003TaxRate = CAPITAL_GAIN_DEFAULT_TAX_RATES.realFrom2003Rate,
    realUntil2002TaxRate = CAPITAL_GAIN_DEFAULT_TAX_RATES.realUntil2002Rate,
    advancePaid = 0,
}) {
    const isCapitalLoss = Number(totalCapitalGain) <= 0;
    const inflRate = Number(inflationaryTaxRate) || 0;
    const from2003Rate = Number(realFrom2003TaxRate) || 0;
    const until2002Rate = Number(realUntil2002TaxRate) || 0;
    const advance = Number(advancePaid) || 0;

    const safeInflationaryTaxableGain = isCapitalLoss
        ? 0
        : Math.max(Number(inflationaryTaxableGain) || 0, 0);
    const safeRealFrom2003 = isCapitalLoss ? 0 : Math.max(Number(realFrom2003) || 0, 0);
    const safeRealUntil2002 = isCapitalLoss ? 0 : Math.max(Number(realUntil2002) || 0, 0);

    const taxOnInflationary = (safeInflationaryTaxableGain * inflRate) / 100;
    const taxOnRealFrom2003 = (safeRealFrom2003 * from2003Rate) / 100;
    const taxOnRealUntil2002 = (safeRealUntil2002 * until2002Rate) / 100;
    const totalTax = taxOnInflationary + taxOnRealFrom2003 + taxOnRealUntil2002;
    const balanceDue = Math.max(totalTax - advance, 0);

    return {
        inflationaryTaxableGain: safeInflationaryTaxableGain,
        realFrom2003: safeRealFrom2003,
        realUntil2002: safeRealUntil2002,
        taxOnInflationary,
        taxOnRealFrom2003,
        taxOnRealUntil2002,
        totalTax,
        balanceDue,
    };
}
